import React from "react";
import { mergeStyleSets } from "@fluentui/react";
import { X, Database, Paperclip } from "@phosphor-icons/react";
import type { Agent } from "../../types/agent.types";

export interface AttachedFile {
  name: string;
  size: number;
  content: string; // text/CSV content (client-side read)
}

export interface DatasetContextInfo {
  tables: string[];
  dept: string;
  agentId?: string; // mapped agent for this department (used for API routing)
}

interface Props {
  selectedAgent: Agent | null;
  attachedFiles: AttachedFile[];
  datasetContext?: DatasetContextInfo | null;
  onClearAgent: () => void;
  onRemoveFile: (index: number) => void;
  onClearDataset?: () => void;
  /** PR 7 — agent chip is now the click target for the persona picker. */
  agentChipRef?: React.RefObject<HTMLDivElement>;
  onAgentChipClick?: () => void;
}

/** Resolve persona ID → { initial, gradient, tier tint } for the chip's
 *  mini-avatar + outer pill background. Mirrors personaVisual in
 *  UnifiedChat.tsx so the chip's identity matches the message avatar. */
function chipVisualFor(agent: Agent): { initial: string; gradient: string; pillBg: string; pillBorder: string; pillFg: string; closeFg: string } {
  switch (agent.id) {
    case "finance":
      return {
        initial: "F",
        gradient: "linear-gradient(140deg, #1f6b2c, #2e873d)",
        pillBg: "#f3fcf3",
        pillBorder: "#c9eccc",
        pillFg: "#1f6b2c",
        closeFg: "#1f6b2c",
      };
    case "assets":
      return {
        initial: "A",
        gradient: "linear-gradient(140deg, #2e873d, #55a655)",
        pillBg: "#f3fcf3",
        pillBorder: "#c9eccc",
        pillFg: "#2e873d",
        closeFg: "#2e873d",
      };
    case "abs":
      return {
        initial: "B",
        gradient: "linear-gradient(140deg, #2563eb, #4f8ff0)",
        pillBg: "#eaf1ff",
        pillBorder: "#c9d8f0",
        pillFg: "#2563eb",
        closeFg: "#2563eb",
      };
    default:
      return {
        initial: (agent.name || "G").trim().charAt(0).toUpperCase() || "G",
        gradient: "linear-gradient(140deg, #2e873d, #55a655)",
        pillBg: "#f3fcf3",
        pillBorder: "#c9eccc",
        pillFg: "#2e873d",
        closeFg: "#2e873d",
      };
  }
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const styles = mergeStyleSets({
  wrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    maxWidth: 820,
    width: "100%",
    margin: "0 auto",
    padding: "0 24px",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 10px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    border: "1px solid #E1E1E1",
    background: "#F5F5F5",
    color: "#232323",
    maxWidth: 240,
    overflow: "hidden",
  },
  chipLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  closeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    borderRadius: 4,
    border: "none",
    background: "transparent",
    color: "#747474",
    cursor: "pointer",
    padding: 0,
    fontSize: 10,
    flexShrink: 0,
    selectors: {
      ":hover": {
        background: "#EDEDED",
        color: "#232323",
      },
    },
  },

  /* PR 7 — branded agent chip with mini-avatar. Clickable to open the
     persona picker. Rounded pill shape, persona-tinted bg, subtler ×. */
  agentChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 10px 4px 4px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    maxWidth: 320,
    cursor: "pointer",
    transition: "border-color 0.12s, box-shadow 0.12s",
    selectors: {
      ":hover": {
        boxShadow: "0 0 0 3px rgba(46,135,61,0.08)",
      },
      ":focus-visible": {
        outline: "none",
        boxShadow: "0 0 0 3px rgba(46,135,61,0.18)",
      },
    },
  } as any,
  agentChipMini: {
    width: 20,
    height: 20,
    borderRadius: 50,
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    letterSpacing: "0.01em",
    boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.06)",
  },
  agentChipLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    letterSpacing: "-0.005em",
  } as any,
  agentChipScope: {
    fontSize: 9.5,
    fontWeight: 700,
    color: "#9C6B00",
    background: "#fbf3df",
    border: "1px solid #f0e0b8",
    padding: "0 5px",
    borderRadius: 999,
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
  },
  agentChipClose: {
    width: 18,
    height: 18,
    borderRadius: 50,
    border: 0,
    background: "transparent",
    cursor: "pointer",
    opacity: 0.55,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "opacity 0.12s, background 0.12s",
    flexShrink: 0,
  } as any,
});

export default function ContextChips({
  selectedAgent,
  attachedFiles,
  datasetContext,
  onClearAgent,
  onRemoveFile,
  onClearDataset,
  agentChipRef,
  onAgentChipClick,
}: Props) {
  const hasDataset = datasetContext && datasetContext.tables.length > 0;
  if (!selectedAgent && attachedFiles.length === 0 && !hasDataset) return null;

  return (
    <div className={styles.wrap}>
      {/* Dataset context chip */}
      {hasDataset && (
        <div
          className={styles.chip}
          style={{
            borderColor: "#A5D6A7",
            background: "#E8F5E9",
            maxWidth: 360,
          }}
        >
          <Database size={12} color="#107C10" weight="regular" />
          <span className={styles.chipLabel}>
            {datasetContext!.dept} · {datasetContext!.tables.join(", ")}
          </span>
          {onClearDataset && (
            <button
              className={styles.closeBtn}
              onClick={onClearDataset}
              title="Remove dataset context"
            >
              <X size={9} weight="bold" />
            </button>
          )}
        </div>
      )}

      {selectedAgent && (() => {
        // PR 7 — branded persona chip. Click anywhere on the chip (except
        // the ×) opens the agent picker. Mini-avatar inside the chip
        // mirrors the persona's identity colour + initial.
        const vis = chipVisualFor(selectedAgent);
        const isScopeAware = selectedAgent.id === "finance";
        return (
          <div
            ref={agentChipRef}
            role={onAgentChipClick ? "button" : undefined}
            tabIndex={onAgentChipClick ? 0 : undefined}
            className={styles.agentChip}
            style={{
              background: vis.pillBg,
              border: `1px solid ${vis.pillBorder}`,
              color: vis.pillFg,
            }}
            onClick={(e) => {
              if (!onAgentChipClick) return;
              // Don't open the picker if the click was on the × button.
              const target = e.target as HTMLElement;
              if (target.closest("[data-agent-chip-close]")) return;
              onAgentChipClick();
            }}
            onKeyDown={(e) => {
              if (!onAgentChipClick) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onAgentChipClick();
              }
            }}
            title={onAgentChipClick ? `${selectedAgent.name} — click to switch persona` : selectedAgent.name}
          >
            <span className={styles.agentChipMini} style={{ background: vis.gradient }} aria-hidden="true">
              {vis.initial}
            </span>
            <span className={styles.agentChipLabel}>{selectedAgent.name}</span>
            {isScopeAware && (
              <span className={styles.agentChipScope}>scope</span>
            )}
            <button
              data-agent-chip-close
              className={styles.agentChipClose}
              onClick={(e) => {
                e.stopPropagation();
                onClearAgent();
              }}
              title="Clear persona"
              aria-label="Clear persona"
              style={{ color: vis.closeFg }}
            >
              <X size={11} weight="bold" />
            </button>
          </div>
        );
      })()}

      {attachedFiles.map((file, i) => (
        <div key={`${file.name}-${i}`} className={styles.chip}>
          <Paperclip size={12} color="#2e873d" weight="regular" />
          <span className={styles.chipLabel}>{file.name}</span>
          <span style={{ color: "#747474", fontSize: 10, flexShrink: 0 }}>
            {formatSize(file.size)}
          </span>
          <button
            className={styles.closeBtn}
            onClick={() => onRemoveFile(i)}
            title="Remove file"
          >
            <X size={9} weight="bold" />
          </button>
        </div>
      ))}
    </div>
  );
}
